'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import SiteAnnouncementBanner from '@/components/layout/SiteAnnouncementBanner';
import {
    buildAnnouncementDismissalKey,
    isAdminAnnouncementPath,
    normalizeActiveAnnouncement,
} from '@/lib/announcementBanner.mjs';

const ACTIVE_ANNOUNCEMENT_ENDPOINT = '/api/site-announcement/active';

function shouldSuppressAnnouncement(pathname) {
    if (isAdminAnnouncementPath(pathname)) {
        return true;
    }

    return (
        pathname === '/fieldays-tackle-haul' ||
        pathname?.startsWith('/fieldays-tackle-haul/')
    );
}

export default function ActiveAnnouncementBanner({ onHeightChange }) {
    const pathname = usePathname();
    const [announcement, setAnnouncement] = useState(null);

    useEffect(() => {
        if (!announcement) {
            onHeightChange?.(0);
        }
    }, [announcement, onHeightChange]);

    useEffect(() => {
        if (shouldSuppressAnnouncement(pathname)) {
            setAnnouncement(null);
            return;
        }

        let mounted = true;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const fetchAnnouncement = async () => {
            try {
                const response = await fetch(ACTIVE_ANNOUNCEMENT_ENDPOINT, {
                    cache: 'no-store',
                    signal: controller.signal,
                });

                clearTimeout(timeoutId);

                if (
                    response.status === 404 ||
                    response.status === 405 ||
                    response.status === 501
                ) {
                    if (mounted) {
                        setAnnouncement(null);
                    }
                    return;
                }

                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch announcement (${response.status})`,
                    );
                }

                const payload = await response.json();
                const nextAnnouncement = normalizeActiveAnnouncement(
                    payload?.announcement ?? payload,
                );

                if (!mounted) {
                    return;
                }

                if (!nextAnnouncement) {
                    setAnnouncement(null);
                    return;
                }

                const dismissalKey =
                    buildAnnouncementDismissalKey(nextAnnouncement);

                if (
                    nextAnnouncement.dismissible &&
                    dismissalKey &&
                    window.sessionStorage.getItem(dismissalKey) === '1'
                ) {
                    setAnnouncement(null);
                    return;
                }

                setAnnouncement(nextAnnouncement);
            } catch (error) {
                console.warn(
                    '[announcement-banner] Failed to fetch active announcement',
                    error,
                );

                if (mounted) {
                    setAnnouncement(null);
                }
            }
        };

        fetchAnnouncement();

        return () => {
            mounted = false;
            controller.abort();
            clearTimeout(timeoutId);
        };
    }, [pathname]);

    if (shouldSuppressAnnouncement(pathname) || !announcement) {
        return null;
    }

    return (
        <SiteAnnouncementBanner
            announcement={announcement}
            isFloating
            onHeightChange={onHeightChange}
            onDismiss={
                announcement.dismissible
                    ? () => {
                          const dismissalKey =
                              buildAnnouncementDismissalKey(announcement);

                          if (dismissalKey) {
                              window.sessionStorage.setItem(dismissalKey, '1');
                          }

                          setAnnouncement(null);
                      }
                    : undefined
            }
        />
    );
}
